import React, { useContext, useEffect, useState } from 'react';
import ReactGA from 'react-ga';
import { registerTagmanagerEvent, TagManagerEvent } from 'configs/trackers';
import { ProfileContext } from 'providers/profile.provider';
import Loading from 'components/loading';
import {
    LoanApplication,
    PartnerStatus,
    useCreateLoanApplicationMutation,
    useGetLoanLazyQuery,
    useGetPartnerByBizIdLazyQuery,
    useSetAmountRequestedMutation,
    useSetUtmsInfoMutation
} from 'generated/graphql';
import { useNavigation, useNeuroId} from 'hooks/index';
import { getQueryParamsAsJson, getTrackingInfo } from 'utils/utils';
import { urlHelper } from 'bizcap-commons-web/dist/url';
import { LoanApplicationContext } from 'containers/context/useGetLoanApplication';
import AmountRequestedLayout from './amount-requested-layout';

const AmountRequested: React.FC = () => {
    const [createLoanApplication] = useCreateLoanApplicationMutation();
    const [setAmountRequested] = useSetAmountRequestedMutation();
    const [setUtmsInfo] = useSetUtmsInfoMutation();
    const [buttonLoading, setButtonLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(false);
    const [loanApplicationData, setLoanApplicationData] = useState<LoanApplication>();

    const { nextScreen } = useNavigation();
    const { steps } = useContext(ProfileContext);
    const { setUserId } = useNeuroId();
    const {loanApplicationId,setLoanApplicationId} = useContext(LoanApplicationContext);
    const utmSource = getQueryParamsAsJson()['utm_source'];
    const [getLoanQuery] = useGetLoanLazyQuery();
    const [getPartnerByBizId] = useGetPartnerByBizIdLazyQuery({fetchPolicy: 'network-only'});
    const { goToGenericError } = useNavigation();

    useEffect(() => {
        ReactGA.event({
            category: 'Navegação Direto',
            action: 'Exibição',
            label: 'Exibição /pedido/',
            nonInteraction: true,
        });
    }, []);

    useEffect(() => {
        if (utmSource) {
            const getPartnerStatus = async () => {
                const partner = await getPartnerByBizId({
                    variables:{
                        bizId: utmSource
                    }
                });

                if (partner?.data?.getPartnerByBizId.status !== PartnerStatus.Active) {
                    goToGenericError();
                }
            }
            getPartnerStatus()
        }
    }, [utmSource]);

    useEffect(() => {
        if(!loanApplicationId) {
            return;
        }
        const loadLoanApplication = async () => {
            await getLoanQuery({
                variables:{
                   id:loanApplicationId
                }
            }).then(({ data: { loanApplication } }) => (
                setLoanApplicationData(loanApplication)
            ));
        }
        loadLoanApplication();
    },[loanApplicationId])


    useEffect(() => {
        const urlBuilder = urlHelper(window.location.href);
        const loanParam = urlBuilder.queryParams.find(
            param => param.key === 'loan_amount'
        );
        if (loanParam && !loanApplicationData && !pageLoading) {
            setPageLoading(true);
            const amountRequested = Number(loanParam.value);
            if (amountRequested <= 500000) {
                create({ amountRequested }).then(nextScreen);
            } else {
                setPageLoading(false);
            }
        }
    }, [loanApplicationData,pageLoading]);

    const onSubmit = async (loanApplicationObj: LoanApplication) => {
        const action = loanApplicationObj.id ? update : create;

        setButtonLoading(true);
        await action(loanApplicationObj);
        nextScreen();
        registerTagmanagerEvent(TagManagerEvent.ORDER);
    };

    const create = async ({ amountRequested }: LoanApplication) => {
        const {
            data: {
                createLoanApplication: { id },
            },
        } = await createLoanApplication({
            variables: { loanApplication: { amountRequested } },
        });
        setUserId(id);
        setLoanApplicationId(id);
        updateUtmInfo(id);
    };

    const update = async ({ id, amountRequested }: LoanApplication) => {
        setUserId(id);
        setAmountRequested({
            variables: {
                loanApplicationId: id,
                loanApplication: { amountRequested },
            },
        });

        updateUtmInfo(id);
    };

    const updateUtmInfo = (id:string) => {

        const trackers = getTrackingInfo();

        setUtmsInfo({ variables: { loanApplicationId: id, trackers } });
    }

    if (pageLoading) {
        return <Loading />;
    }

    return (
        <AmountRequestedLayout
            initialValue={loanApplicationData ? loanApplicationData: {}}
            stepInfo={steps.amountRequested}
            onSubmit={onSubmit}
            loading={buttonLoading}
        />
    );
};

export default AmountRequested;
